/**
 * Utilitaires partages pour lire les informations de pagination renvoyees
 * par les endpoints backend. Le payload peut contenir n'importe quelle
 * collection metier (`tickets`, `alarmes`, etc.) accompagnee d'un bloc
 * `pagination`.
 *
 * Ce module centralise l'extraction des metadonnees de page afin d'eviter
 * de dupliquer la meme logique dans les composants et services.
 */

export type PaginationMeta = {
  page?: number;
  limit?: number;
  total?: number;
  pages?: number;
  /** Total des tickets comportant des messages non lus, toutes pages confondues. */
  tickets_messages_non_lus?: number;
};

export type PaginationPayload = {
  [key: string]: unknown;
  pagination?: PaginationMeta;
};

/**
 * Extrait le nombre total d'elements depuis un payload pagine.
 * Si le backend ne fournit pas `pagination.total`, on retombe sur
 * la taille du premier tableau metier trouve dans le payload.
 */
export function extraireTotalPagination(response: PaginationPayload | null): number | undefined {
  if (typeof response?.pagination?.total === 'number') {
    return response.pagination.total;
  }

  const collection = extraireCollectionPaginee(response);
  return collection?.length;
}

/**
 * Extrait le numero de page courant depuis un payload pagine.
 */
export function extrairePagePagination(response: PaginationPayload | null): number | undefined {
  return typeof response?.pagination?.page === 'number' ? response.pagination.page : undefined;
}

/**
 * Extrait le nombre total de pages depuis un payload pagine.
 */
export function extraireTotalPagesPagination(response: PaginationPayload | null): number {
  return typeof response?.pagination?.pages === 'number' ? response.pagination.pages : 0;
}

/**
 * Retourne la premiere collection metier trouvee dans un payload pagine,
 * en ignorant la cle `pagination`.
 */
export function extraireCollectionPaginee(
  response: PaginationPayload | null,
): unknown[] | undefined {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  for (const [cle, valeur] of Object.entries(response)) {
    if (cle === 'pagination') {
      continue;
    }

    if (Array.isArray(valeur)) {
      return valeur;
    }
  }

  return undefined;
}
